/**
 * The other half of cross-device progress sync: while progressSync.ts
 * pushes THIS device's own marks up, this pulls down what other devices
 * have marked on a job already sitting locally — the piece that lets two
 * photographers working the same job see each other's progress.
 *
 * Deliberately a plain "fetch the current status of every subject and
 * merge" rather than a delta/changes-since feed: jobs are small enough
 * (tens to a few hundred subjects) that this stays cheap, and it sidesteps
 * an entire class of "missed a delta" bugs a cursor-based design would
 * risk. See server/db/jobsRepo.ts's getJobStatusSnapshot for the endpoint
 * this calls — ids/status/updatedAt only, no names or QR payloads, since
 * this may run every 15-30s while online.
 */
import { api } from '../api/client';
import { db } from '../db/db';

interface JobStatusSnapshotDto {
  completedSubjects: number;
  totalSubjects: number;
  subjects: Array<{ id: string; status: 'not_photographed' | 'photographed' | 'excluded'; updatedAt: string }>;
}

/**
 * Pulls the latest status for every subject in `jobId` and merges any
 * change that's actually newer than this device's own local copy into
 * IndexedDB — existing `useLiveQuery` hooks throughout the app pick the
 * change up automatically, no extra plumbing needed.
 *
 * Two things are deliberately never overwritten by this:
 *  - A subject with a *pending* (not yet pushed) local progress event —
 *    this device's own not-yet-synced change is always more current than
 *    a snapshot that, by definition, can't know about it yet.
 *  - A subject whose local `updatedAt` is already newer than the
 *    snapshot's (the same last-write-wins rule the server applies).
 *
 * Best-effort: never throws, no-ops while offline.
 */
export async function pullJobStatus(jobId: string): Promise<{ applied: number }> {
  if (!navigator.onLine) return { applied: 0 };

  try {
    const snapshot = await api.get<JobStatusSnapshotDto>(`/api/jobs/${jobId}/status`, 10_000);

    const pendingSubjectIds = new Set(
      (await db.progressEvents.where('jobId').equals(jobId).and(e => e.syncState === 'pending').toArray()).map(
        e => e.subjectId
      )
    );

    let applied = 0;
    await db.transaction('rw', db.subjects, db.jobs, async () => {
      for (const remote of snapshot.subjects) {
        if (pendingSubjectIds.has(remote.id)) continue; // this device's own in-flight change wins

        const local = await db.subjects.get(remote.id);
        if (!local) continue; // not part of this job locally (shouldn't happen, but not this function's job to fix)
        if (new Date(remote.updatedAt).getTime() <= new Date(local.updatedAt).getTime()) continue;

        await db.subjects.update(remote.id, { status: remote.status, updatedAt: remote.updatedAt });
        applied++;
      }

      if (applied > 0) {
        await db.jobs.update(jobId, { completedSubjects: snapshot.completedSubjects });
      }
    });

    return { applied };
  } catch {
    // Offline, backend not deployed, job purged, etc. — silently skip;
    // the next scheduled poll tries again.
    return { applied: 0 };
  }
}
